import * as Sentry from '@sentry/react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_URL } from '../config/api';

// Current Cognito access token (Amplify refreshes it as needed). Used to
// authenticate user-scoped requests so the backend can verify the caller.
const getAccessToken = async () => {
  // Returns null when there is cleanly no session; THROWS on a transient failure
  // (network/refresh blip) so callers can distinguish "signed out" from "try again".
  const session = await fetchAuthSession();
  return session.tokens?.accessToken?.toString() || null;
};

// Default timeout for quick endpoints. The council run is long, so it gets its own.
const DEFAULT_TIMEOUT_MS = 30000;
const SUBMIT_TIMEOUT_MS = 120000; // panel discussion can take a while
const PDF_TIMEOUT_MS = 60000;

// Error type that distinguishes a transient failure (network/timeout/5xx — safe to
// retry, callers should keep last-known-good state) from a definitive 4xx response.
export class ApiError extends Error {
  constructor(message, { status = null, transient = false, cause } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.transient = transient;
    if (cause) this.cause = cause;
  }
}

// Single fetch wrapper: adds an AbortController timeout, normalizes errors, and
// reports transient failures to Sentry with endpoint context.
const request = async (path, { method = 'GET', body, timeout = DEFAULT_TIMEOUT_MS, captureContext = {}, alwaysCapture = false, auth = false } = {}) => {
  const headers = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth) {
    let token;
    try {
      token = await getAccessToken();
    } catch (err) {
      // Couldn't reach Amplify to verify the session (network/refresh blip). Surface
      // as retryable instead of forcing a sign-out on a still-valid user.
      throw new ApiError('Network error while verifying your session — please try again.', { transient: true, cause: err });
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else if (auth === 'required') {
      // Cleanly no session token for a logged-in caller -> force re-auth rather than
      // silently sending an unauthenticated request the backend will reject.
      throw new ApiError('Your session has expired. Please sign in again to continue.', { status: 401 });
    }
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);

  let res;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method,
      headers: Object.keys(headers).length ? headers : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (err) {
    const timedOut = err.name === 'AbortError';
    const apiErr = new ApiError(
      timedOut
        ? 'This is taking longer than expected. Please try again in a moment.'
        : 'Network error — please check your connection and try again.',
      { transient: true, cause: err }
    );
    Sentry.captureException(apiErr, { extra: { path, method, timedOut, ...captureContext } });
    throw apiErr;
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    const transient = res.status >= 500;
    const apiErr = new ApiError(
      errData.error || `Request failed${res.status ? ` (${res.status})` : ''}`,
      { status: res.status, transient }
    );
    if (transient || alwaysCapture) {
      Sentry.captureException(apiErr, { extra: { path, method, status: res.status, ...captureContext } });
    }
    throw apiErr;
  }

  return res.json();
};

// Get subscription status for a user.
// A clean response (even "no subscription") is authoritative. A transient failure
// throws so the caller can keep the last-known-good plan instead of downgrading.
export const getSubscriptionStatus = async (cognitoSub) => {
  if (!cognitoSub) return null;
  try {
    return await request(`/subscription/status?cognito_sub=${encodeURIComponent(cognitoSub)}`, {
      timeout: 15000,
      auth: true,
      captureContext: { feature: 'subscription-status' },
    });
  } catch (err) {
    // 4xx means the backend answered: no subscription on record -> treat as free tier.
    if (err instanceof ApiError && err.status && err.status < 500) return null;
    // Network/timeout/5xx -> we genuinely don't know; let the caller decide.
    throw err;
  }
};

// Create a Stripe Checkout session.
// alwaysCapture: any failure on the revenue path is worth a Sentry event,
// not just transient ones.
export const createCheckoutSession = ({ cognitoSub, email, tier }) =>
  request('/subscription/checkout', {
    method: 'POST',
    body: { cognito_sub: cognitoSub, email, tier },
    auth: true,
    captureContext: { feature: 'checkout', tier },
    alwaysCapture: true,
  });

// Create a Stripe Customer Portal session
export const createPortalSession = (cognitoSub) =>
  request('/subscription/portal', {
    method: 'POST',
    body: { cognito_sub: cognitoSub },
    auth: true,
    captureContext: { feature: 'portal' },
    alwaysCapture: true,
  });

// Permanently delete the user's account (cancels any Stripe subscription,
// purges their data, and removes their login). Irreversible.
export const deleteAccount = (cognitoSub) =>
  request(`/account?cognito_sub=${encodeURIComponent(cognitoSub)}`, {
    method: 'DELETE',
    auth: true,
    captureContext: { feature: 'delete-account' },
    alwaysCapture: true,
  });

// Generic API submission for tools
export const submitToolRequest = ({ prompt, personas, user = {}, toolId, includeFullAnalysis = false, additionalRecipients = [] }) => {
  const payload = {
    prompt,
    rounds: 3,
    synthesis: true,
    async_mode: true,
    personas,
    include_full_analysis: includeFullAnalysis,
  };

  // User info for auto-email
  if (user.sub) payload.cognito_sub = user.sub;
  if (user.email) payload.email = user.email;
  if (user.displayName) payload.display_name = user.displayName;

  // Additional recipients (Team tier)
  if (additionalRecipients.length > 0) payload.additional_recipients = additionalRecipients;

  return request('/panel-discussion', {
    method: 'POST',
    body: payload,
    timeout: SUBMIT_TIMEOUT_MS,
    // Verify-if-claimed: a logged-in user must send a valid token (forces re-auth
    // if their session expired); anonymous users submit without auth as before.
    auth: user.sub ? 'required' : false,
    captureContext: { feature: 'panel-discussion', toolId },
  });
};

// Submit open discussion
export const submitDiscussion = ({ prompt, personas, user, includeFullAnalysis = false, additionalRecipients = [] }) =>
  submitToolRequest({
    prompt,
    personas,
    user,
    toolId: 'discussion',
    includeFullAnalysis,
    additionalRecipients,
  });

// Get discussion history for authenticated Pro/Team user
export const getDiscussionHistory = (cognitoSub, { limit = 20, offset = 0 } = {}) =>
  request(
    `/panel-discussion/history?cognito_sub=${encodeURIComponent(cognitoSub)}&limit=${limit}&offset=${offset}`,
    { auth: true, captureContext: { feature: 'history' } }
  );

// Download PDF for a completed discussion
export const downloadDiscussionPdf = (discussionId, cognitoSub) =>
  request(
    `/panel-discussion/${discussionId}/pdf?cognito_sub=${encodeURIComponent(cognitoSub)}`,
    { timeout: PDF_TIMEOUT_MS, auth: true, captureContext: { feature: 'pdf' } }
  );

// Resend discussion email with PDF attachment
export const resendDiscussionEmail = (discussionId, { cognitoSub, email, displayName }) =>
  request(`/panel-discussion/${discussionId}/email`, {
    method: 'POST',
    body: { cognito_sub: cognitoSub, email, display_name: displayName },
    auth: true,
    captureContext: { feature: 'resend-email' },
  });

// Send email for existing discussion results
export const sendDiscussionEmail = ({ discussionData, user }) =>
  request('/email', {
    method: 'POST',
    body: { discussion: discussionData, email: user.email },
    captureContext: { feature: 'send-email' },
  });
